import React from 'react';
import { Box, Card, CardContent, Chip, Typography } from '@mui/material';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import { DASHBOARD_KEYS } from '../../../../platform/widgets/widgetRegistry.js';
import { getBandForIndex } from '../../../../assistants/hugo/barometerService';
import { useBarometer } from '../../../../assistants/hugo/useBarometer';
import { FEATURE_HUGO } from '../../../../config/features';
import RoleDashboardPage from './RoleDashboardPage';

/**
 * Executive Dashboard — Mindmap branch 4/5, "Executive / Site Manager"
 *  - Enterprise REPS, sites, critical alerts, divergences, reports
 * Widgets (B4): enterprise-scores, risk-alerts, what-changed, comparisons (by site), sites-glance,
 * divergences, oe-trend, report-shortcuts, oe-conditions (all sites). The Barometer card returns
 * with HUGO (fixed above the grid while `VITE_FEATURE_HUGO` is on).
 */
function BarometerBanner({ orgId }) {
  const { barometer } = useBarometer(orgId);
  const baroIndex = barometer?.index || null;
  const band = baroIndex ? getBandForIndex(baroIndex.current) : null;
  return (
    <Card sx={{ mb: 3, borderLeft: 6, borderLeftColor: band?.color || 'divider' }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="overline" color="text.secondary">HP Barometer</Typography>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
            <Typography variant="h3" fontWeight={700}>{baroIndex ? baroIndex.current : '—'}</Typography>
            <Typography variant="body2" color="text.secondary">/ 100</Typography>
          </Box>
        </Box>
        {band && <Chip label={band.label} size="small" sx={{ bgcolor: band.color, color: '#fff' }} />}
        <Typography variant="caption" color="text.secondary">
          {baroIndex ? `${baroIndex.delta30Days > 0 ? '+' : ''}${baroIndex.delta30Days} pts (30d)` : 'needs survey data'}
        </Typography>
      </CardContent>
    </Card>
  );
}

function ExecutiveDashboard() {
  return (
    <RoleDashboardPage
      dashboardKey={DASHBOARD_KEYS.drewExec}
      hero={{
        icon: BusinessCenterIcon,
        title: 'Executive Dashboard',
        color: '#0d47a1',
        caption: (role) => {
          const siteCount = role.comparisons?.siteScores?.length || 0;
          const regionCount = role.comparisons?.regions?.length || 0;
          return (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Enterprise: <strong>{role.comparisons?.org?.name || role.activeOrg?.name || '—'}</strong>
              {' • '}{siteCount} site{siteCount === 1 ? '' : 's'}
              {regionCount > 0 ? ` across ${regionCount} region${regionCount === 1 ? '' : 's'}` : ''}
            </Typography>
          );
        },
      }}
      conditions={{ title: 'OE Conditions', scopeLabel: 'all sites' }}
      banner={(role) => (FEATURE_HUGO ? <BarometerBanner orgId={role.activeOrg?.id || null} /> : null)}
    />
  );
}

export default ExecutiveDashboard;
